import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ExternalLink, Rocket, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";
import { launchVerticals } from "../api";

/**
 * /launch/showcase — live customer sites we've built.
 * Reads sites_built from GET /api/launch/verticals so the list is
 * server-owned and can grow without a redeploy.
 */
export default function LaunchShowcasePage() {
  const [sites, setSites] = useState(null);

  useEffect(() => {
    launchVerticals().then((d) => setSites(d.sites_built || []));
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-sky-50 to-white text-slate-900" data-testid="launch-showcase-page">
      <div className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Badge className="bg-emerald-100 border border-emerald-300 text-emerald-800 uppercase tracking-widest text-[10px] font-bold">
              <ShieldCheck size={11} className="mr-1" /> Live customer sites
            </Badge>
            <h1 className="mt-4 text-4xl sm:text-5xl font-black">Real sites we've built.</h1>
            <p className="mt-3 text-slate-600 max-w-2xl">
              Every site here launched through the WebsiteTech starter program —
              AI-drafted, customer-verified, human-approved before it went public.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/launch/designs">
              <Button variant="outline" className="border-slate-300 text-slate-700 bg-white">
                <Sparkles size={14} className="mr-1" /> Design board
              </Button>
            </Link>
            <Link to="/launch">
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="showcase-start-cta">
                <Rocket size={14} className="mr-1" /> Get my free AI preview
              </Button>
            </Link>
          </div>
        </div>

        {sites === null && (
          <div className="mt-16 text-center"><Loader2 className="mx-auto animate-spin text-sky-600" /></div>
        )}

        {sites && sites.length === 0 && (
          <Card className="mt-12 p-10 text-center bg-white border-dashed border-slate-300">
            <div className="text-slate-500">First customer sites launch this week.</div>
            <div className="text-xs text-slate-400 mt-1">Check back soon or start your own preview now.</div>
          </Card>
        )}

        {sites && sites.length > 0 && (
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {sites.map((s) => (
              <Link key={s.slug} to={`/launch/showcase/${s.slug}`}
                 data-testid={`showcase-card-${s.slug}`}
                 className="group rounded-2xl overflow-hidden border border-slate-200 bg-white shadow-sm hover:shadow-lg hover:border-sky-400 transition">
                <div className="aspect-[16/10] overflow-hidden bg-slate-100 relative">
                  <img src={s.screenshot} alt={`${s.name} homepage`}
                       className="w-full h-full object-cover object-top group-hover:scale-[1.02] transition-transform duration-500" />
                  <div className="absolute top-3 left-3 bg-white/95 px-2 py-1 rounded text-[10px] font-bold uppercase tracking-widest text-slate-700">
                    Live site
                  </div>
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{s.vertical_label || s.vertical}</div>
                  <div className="mt-1 text-slate-900 font-bold text-lg flex items-center gap-1.5">
                    {s.name}
                    <ArrowRight size={13} className="text-slate-400 group-hover:text-sky-600 group-hover:translate-x-0.5 transition" />
                  </div>
                  <div className="text-xs text-sky-700 mt-0.5 truncate">{s.url.replace(/^https?:\/\//, "")}</div>
                  <div className="text-sm text-slate-600 mt-3 leading-relaxed line-clamp-3">{s.blurb}</div>
                  <div className="mt-3 text-xs font-bold text-sky-700 group-hover:text-sky-900">
                    Read the case study →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 rounded-2xl border border-sky-200 bg-sky-50 p-6 sm:p-8 flex flex-wrap gap-6 items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-800 font-bold">Your business is next</div>
            <div className="mt-1 text-xl font-black text-slate-900">Apply in 3 minutes — see your preview today.</div>
          </div>
          <Link to="/launch">
            <Button size="lg" className="bg-slate-900 text-white hover:bg-slate-800 font-bold">
              Start my free AI preview <ArrowRight size={16} className="ml-1" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
